import React, { useMemo, useState } from 'react';
import { Box, Flex, Popover, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconBellFilled } from '@tabler/icons-react';
import { useQuery } from '@apollo/client';
import { Link, useLocation } from 'react-router-dom';

import NavbarUser from './NavbarUser';
import NotificationDrawer from '../notifications/NotificationDrawer';
import logo from '../../assets/cb-element.svg';
import fullLogo from '../../assets/cb-logo-white.png';
import { GET_UNREAD_NOTIFICATION_COUNT } from '../../apollo/mutations/notification';
import { useRoleModules } from '../../hooks/useRoleModules';
import ModuleSelectionItem from '../common/ModuleSelectionItem';
import StandInStatus from './StandInStatus';

function Navbar() {
    const { pathname } = useLocation();
    const modulesData = useRoleModules();

    const isDashboard = pathname.includes('dashboard');

    const selectedModule = useMemo(
        () =>
            modulesData.find(moduleData => pathname.includes(moduleData.link)),
        [modulesData, pathname],
    );

    const { data: countData } = useQuery(GET_UNREAD_NOTIFICATION_COUNT, {
        fetchPolicy: 'network-only',
        pollInterval: 10000,
    });

    const unReadNotificationsCount = countData?.getUnreadNotificationCount ?? 0;

    const [showNotificationsDrawer, setShowNotificationsDrawer] =
        useState(false);

    return (
        <>
            <div className="flex w-screen h-16 bg-primary px-4 py-3 fixed top-0 z-20">
                <div className="flex flex-row items-center justify-between w-full h-full">
                    <div className="flex items-center gap-3">
                        {isDashboard ? (
                            <img
                                src={fullLogo}
                                alt="logo"
                                className="h-[26px] ml-1"
                            />
                        ) : (
                            <Link to="/dashboard">
                                <img
                                    src={logo}
                                    alt="logo"
                                    className="h-8 aspect-square ml-1"
                                />
                            </Link>
                        )}

                        {!isDashboard && (
                            <Popover
                                position="bottom"
                                withArrow
                                shadow="md"
                                radius="lg"
                            >
                                <Popover.Target>
                                    <Flex
                                        className="cursor-pointer"
                                        gap="8px"
                                        align="center"
                                    >
                                        <Text
                                            className="text-white font-semibold"
                                            ml="md"
                                        >
                                            {selectedModule?.value ||
                                                'Select a Module'}
                                        </Text>
                                        <Box mt="5px">
                                            <IconChevronDown
                                                color="white"
                                                size="16px"
                                            />
                                        </Box>
                                    </Flex>
                                </Popover.Target>
                                <Popover.Dropdown px="4px" py="6px" ml="lg">
                                    {modulesData.length === 0 && (
                                        <Text
                                            className="text-center text-gray-400"
                                            size="sm"
                                        >
                                            You do not have access to any
                                            modules.
                                        </Text>
                                    )}
                                    <Stack gap="2px">
                                        {modulesData.length > 0 &&
                                            modulesData.map(module => (
                                                <ModuleSelectionItem
                                                    key={module.value}
                                                    {...module}
                                                    showShadow={false}
                                                    selected={
                                                        selectedModule?.value ===
                                                        module.value
                                                    }
                                                />
                                            ))}
                                    </Stack>
                                </Popover.Dropdown>
                            </Popover>
                        )}
                    </div>

                    <div className="flex items-center space-x-2 relative z-50">
                        <StandInStatus />
                        <UnstyledButton
                            className="relative cursor-pointer"
                            role="button"
                            onClick={() => setShowNotificationsDrawer(true)}
                        >
                            {unReadNotificationsCount > 0 && (
                                <div
                                    className={`absolute -top-1 ${
                                        unReadNotificationsCount <= 9
                                            ? 'right-[-6px]'
                                            : 'right-[-11px]'
                                    } bg-red-500 rounded-full flex items-center justify-center p-1 h-4`}
                                >
                                    <Text className="text-xs text-white">
                                        {unReadNotificationsCount > 99
                                            ? '99+'
                                            : unReadNotificationsCount}
                                    </Text>
                                </div>
                            )}
                            <IconBellFilled className="text-white" />
                        </UnstyledButton>
                        <NavbarUser />
                    </div>
                </div>
            </div>
            <NotificationDrawer
                opened={showNotificationsDrawer}
                close={() => setShowNotificationsDrawer(false)}
            />
        </>
    );
}

export default Navbar;
